"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createBundleWithCustomFiles = exports.createBundleFromFolders = exports.getSupportedFiles = exports.remoteBundleFactory = exports.uploadRemoteBundle = void 0;
/* eslint-disable no-await-in-loop */
const lodash_pick_1 = __importDefault(require("lodash.pick"));
const lodash_omit_1 = __importDefault(require("lodash.omit"));
const p_map_1 = __importDefault(require("p-map"));
const files_1 = require("./files");
const http_1 = require("./http");
const constants_1 = require("./constants");
const emitter_1 = require("./emitter");
async function* prepareRemoteBundle(options) {
    let response;
    let { bundleHash } = options;
    let cumulativeProgress = 0;
    emitter_1.emitter.createBundleProgress(cumulativeProgress, options.files.length);
    for (const chunkedFiles of files_1.composeFilePayloads(options.files)) {
        const apiParams = {
            ...lodash_pick_1.default(options, [
                'baseURL',
                'sessionToken',
                'source',
                'extraHeaders',
                'removedFiles',
                'requestId',
                'org',
                'orgId',
            ]),
            files: chunkedFiles.reduce((d, f) => {
                // deepcode ignore PrototypePollution: FP this is an internal code
                d[f.bundlePath] = f.hash;
                return d;
            }, {}),
        };
        if (!bundleHash) {
            // eslint-disable-next-line no-await-in-loop
            response = await http_1.createBundle(apiParams);
        }
        else {
            // eslint-disable-next-line no-await-in-loop
            response = await http_1.extendBundle({ bundleHash, ...apiParams });
        }
        cumulativeProgress += chunkedFiles.length;
        emitter_1.emitter.createBundleProgress(cumulativeProgress, options.files.length);
        if (response.type === 'error') {
            // TODO: process Error
            yield response;
            break;
        }
        bundleHash = response.value.bundleHash;
        yield response;
    }
}
/**
 * Splits files in buckets and upload in parallel
 * @param baseURL
 * @param sessionToken
 * @param remoteBundle
 */
async function uploadRemoteBundle(options) {
    let uploadedFiles = 0;
    emitter_1.emitter.uploadBundleProgress(0, options.files.length);
    const apiParams = lodash_pick_1.default(options, [
        'baseURL',
        'sessionToken',
        'source',
        'bundleHash',
        'requestId',
        'org',
        'orgId',
        'extraHeaders',
    ]);
    const uploadFileChunks = async (bucketFiles) => {
        // Note: we specifically create __new__ isolated bundles here to faster files upload
        const resp = await http_1.createBundle({
            ...apiParams,
            files: bucketFiles.reduce((d, f) => {
                d[f.bundlePath] = lodash_pick_1.default(f, ['hash', 'content']);
                return d;
            }, {}),
        });
        if (resp.type !== 'error') {
            uploadedFiles += bucketFiles.length;
            emitter_1.emitter.uploadBundleProgress(uploadedFiles, options.files.length);
        }
    };
    const files = [];
    for (const bucketFiles of files_1.composeFilePayloads(options.files)) {
        files.push(bucketFiles);
    }
    await p_map_1.default(files, async (task) => await uploadFileChunks(task), {
        concurrency: constants_1.UPLOAD_CONCURRENCY,
    });
}
exports.uploadRemoteBundle = uploadRemoteBundle;
async function fullfillRemoteBundle(options) {
    // Fulfill remote bundle by uploading only missing files (splitted in chunks)
    // Check remove bundle to make sure no missing files left
    let attempts = 0;
    let { remoteBundle } = options;
    const connectionOptions = lodash_pick_1.default(options, [
        'baseURL',
        'sessionToken',
        'source',
        'requestId',
        'org',
        'orgId',
        'extraHeaders',
    ]);
    while (remoteBundle.missingFiles.length && attempts < (options.maxAttempts || constants_1.MAX_UPLOAD_ATTEMPTS)) {
        const missingFiles = await files_1.resolveBundleFiles(options.baseDir, remoteBundle.missingFiles);
        await uploadRemoteBundle({
            ...connectionOptions,
            bundleHash: remoteBundle.bundleHash,
            files: missingFiles,
        });
        const bundleResponse = await http_1.checkBundle({ ...connectionOptions, bundleHash: remoteBundle.bundleHash });
        if (bundleResponse.type === 'error') {
            throw new Error('Failed to get remote bundle');
        }
        // eslint-disable-next-line no-param-reassign
        remoteBundle = bundleResponse.value;
        attempts += 1;
    }
    return remoteBundle;
}
async function remoteBundleFactory(options) {
    let remoteBundle = null;
    const baseOptions = lodash_pick_1.default(options, [
        'baseURL',
        'sessionToken',
        'source',
        'baseDir',
        'requestId',
        'org',
        'orgId',
        'extraHeaders',
    ]);
    const bundleFactory = prepareRemoteBundle(lodash_omit_1.default(options, ['baseDir']));
    for await (const response of bundleFactory) {
        if (response.type === 'error') {
            throw response.error;
        }
        remoteBundle = await fullfillRemoteBundle({ ...baseOptions, remoteBundle: response.value });
        if (remoteBundle.missingFiles.length) {
            throw new Error(`Failed to upload # files: ${remoteBundle.missingFiles.length}`);
        }
    }
    return remoteBundle;
}
exports.remoteBundleFactory = remoteBundleFactory;
/**
 * Get supported filters and test baseURL for correctness and availability
 *
 * @param baseURL
 * @param source
 * @returns
 */
async function getSupportedFiles(baseURL, source, requestId, languages, orgId, extraHeaders) {
    emitter_1.emitter.supportedFilesLoaded(null);
    const resp = await http_1.getFilters({
        baseURL,
        source,
        orgId,
        requestId,
        attempts: constants_1.MAX_RETRY_ATTEMPTS,
        extraHeaders: extraHeaders !== null && extraHeaders !== void 0 ? extraHeaders : {},
    });
    if (resp.type === 'error') {
        throw resp.error;
    }
    const supportedFilesFromApi = resp.value;
    //Given supported languages from 'registy'
    if (languages) {
        const supportedFiles = {
            configFiles: supportedFilesFromApi.configFiles,
            extensions: languages,
        };
        //For verification only
        // Make sure we compare file extensions between results from `registry` and `deeproxy` without case sensitivity to avoid missing some of supported extensions.
        const userSupportedExtensions = supportedFilesFromApi.extensions.map(e => e.toLowerCase());
        supportedFiles.extensions = supportedFiles.extensions.filter(langExtension => userSupportedExtensions.includes(langExtension.toLowerCase()));
        emitter_1.emitter.supportedFilesLoaded(supportedFiles);
        return supportedFiles;
    }
    emitter_1.emitter.supportedFilesLoaded(supportedFilesFromApi);
    return supportedFilesFromApi;
}
exports.getSupportedFiles = getSupportedFiles;
/**
 * Creates a remote bundle and returns response from the bundle API
 *
 * @param {CreateBundleFromFoldersOptions} options
 * @returns {Promise<FileBundle | null>}
 */
async function createBundleFromFolders(options) {
    // Fetch supported files to save network traffic
    const supportedFiles = await getSupportedFiles(options.baseURL, options.source, options.requestId, options.languages, options.orgId, options.extraHeaders);
    // Collect files and create a remote bundle
    return await createBundleWithCustomFiles(options, supportedFiles);
}
exports.createBundleFromFolders = createBundleFromFolders;
/**
 * Creates a remote bundle and returns response from the bundle API
 * This function is used to create a bundle with a custom list of supported file extensions
 *
 * @param {CreateBundleFromFoldersOptions} options
 * @param {SupportedFiles} supportedFiles
 * @returns {Promise<FileBundle | null>}
 */
async function createBundleWithCustomFiles(options, supportedFiles) {
    // Scan for custom ignore rules
    const filePolicies = await files_1.collectFilePolicies(options.paths, options.symlinksEnabled, options.defaultFileIgnores);
    const baseDir = files_1.determineBaseDir(options.paths);
    emitter_1.emitter.scanFilesProgress(0);
    const bundleFiles = [];
    const skippedOversizedFiles = [];
    let totalFiles = 0;
    const bundleFileCollector = files_1.collectBundleFiles({
        ...lodash_pick_1.default(options, ['paths', 'symlinksEnabled']),
        baseDir,
        filePolicies,
        supportedFiles,
    });
    for await (const f of bundleFileCollector) {
        typeof f == 'string' ? skippedOversizedFiles.push(f) : bundleFiles.push(f);
        totalFiles += 1;
        emitter_1.emitter.scanFilesProgress(totalFiles);
    }
    const bundleOptions = {
        ...lodash_pick_1.default(options, ['baseURL', 'sessionToken', 'source', 'requestId', 'org', 'orgId', 'extraHeaders']),
        baseDir,
        files: bundleFiles,
    };
    // Create remote bundle
    if (!bundleFiles.length)
        return null;
    const remoteBundle = await remoteBundleFactory(bundleOptions);
    if (remoteBundle === null)
        return null;
    return {
        ...remoteBundle,
        baseDir,
        supportedFiles,
        fileIgnores: [...filePolicies.excludes, ...filePolicies.ignores],
        skippedOversizedFiles,
    };
}
exports.createBundleWithCustomFiles = createBundleWithCustomFiles;
//# sourceMappingURL=bundles.js.map